import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { KeyRound, Layers, User, Map as MapIcon, ArrowRight, ChevronDown, ChevronRight, Calculator } from "lucide-react";
import { PageHeader } from "../components/Layout";
import { InfoTip, Aufklapp } from "../components/Erklaerung";
import { Card, Badge, KlasseBadge } from "../components/ui";
import { RoleName } from "../components/RoleName";
import { PlanBar } from "../components/PlanBar";
import { PlanButton } from "../components/PlanButton";
import { useApp } from "../../store/AppStore";
import { usePlanItems } from "../../store/plan";
import { planResult } from "../../domain/plan";
import { TRACE_META, traceWindowLabel } from "../../data/traceMeta";
import { assignedLandscapes, landscapeById } from "../../data/landscapes";
import { fueLandscapes } from "../../data/licensing";
import { leverRanking, rankedPersons, landscapeDemand, leverBreakdown, crossSystemSummary, comboExample, basketAllFue, leverPersons } from "../../domain/analysis";
import { CLASS_LABEL, formatEuroMio, formatFue, formatNum, formatWeightExact, type RatioTable, weight } from "../../domain/rules";
import type { LeverBreakdown, LeverPersonRow } from "../../domain/analysis";
import type { Dataset } from "../../data/generator";
import type { TraceState } from "../../domain/types";

// Paket 3.1 — die offengelegte Rechnung je Maßnahme (Pflichtleistung AP3.1).
// G1: Wer steckt hinter einem Trace-Balken? Klick öffnet die Personen dieses Bereichs.
function TracePersonen({ rows, trace }: { rows: LeverPersonRow[]; trace: TraceState }) {
  const [n, setN] = useState(100);
  if (rows.length === 0) return <div className="text-faint mt-2 text-xs">Keine Person in diesem Bereich.</div>;
  return (
    <div className="mt-2 overflow-x-auto rounded-lg p-2" style={{ borderWidth: 1, borderColor: "var(--border-soft)" }}>
      <div className="text-faint mb-1 flex flex-wrap items-center gap-x-3 text-[11px] uppercase tracking-wide">
        <span>{formatNum(rows.length)} Personen · {trace === "nie-genutzt" ? "nie genutzt" : trace === "genutzt" ? "genutzt" : "kein Trace"}</span>
        <span className="normal-case">Funktion und Fachbereich aus SU01 (je System gepflegt), Klasse vorher → nachher</span>
      </div>
      <table className="w-full text-xs">
        <thead>
          <tr className="text-faint text-left uppercase tracking-wide">
            <th className="px-2 py-1 font-medium">Person</th><th className="px-2 py-1 font-medium">Name</th><th className="px-2 py-1 font-medium">Funktion</th><th className="px-2 py-1 font-medium">Fachbereich</th><th className="px-2 py-1 font-medium">Konto</th><th className="px-2 py-1 font-medium">Klasse</th><th className="px-2 py-1 text-right font-medium">Δ FUE</th><th className="px-2 py-1 text-right font-medium">Anmeldung</th>
          </tr>
        </thead>
        <tbody>
          {rows.slice(0, n).map((r) => (
            <tr key={r.id} style={{ borderTopWidth: 1, borderColor: "var(--border-soft)" }}>
              <td className="px-2 py-1 font-mono whitespace-nowrap"><Link to={`/berechtigungen?person=${r.id}`} className="hover:underline" style={{ color: "var(--color-brand-600)" }}>{r.id}</Link></td>
              <td className="text-strong px-2 py-1">{r.name ?? "—"}</td>
              <td className="text-dim px-2 py-1">{r.funktion ?? "—"}</td>
              <td className="text-dim px-2 py-1">{r.abteilung ?? "—"}</td>
              <td className="px-2 py-1 font-mono">{r.sid}/{r.client}</td>
              <td className="px-2 py-1"><span className="text-strong">{CLASS_LABEL[r.from]}</span> → {CLASS_LABEL[r.to]}</td>
              <td className="tnum px-2 py-1 text-right" style={{ color: "var(--color-amp-green)" }}>−{formatWeightExact(r.delta)}</td>
              <td className="tnum text-dim px-2 py-1 text-right">{r.lastLogonDays === undefined ? "—" : `vor ${formatNum(r.lastLogonDays)} T.`}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {rows.length > n && (
        <div className="mt-1 flex items-center gap-3 text-xs">
          <span className="text-faint">{formatNum(n)} von {formatNum(rows.length)} gezeigt</span>
          <button type="button" onClick={() => setN((x) => x + 200)} className="font-medium hover:underline" style={{ color: "var(--color-brand-600)" }}>weitere 200</button>
          <Link to="/personen" className="text-faint hover:underline">Personenübersicht</Link>
        </div>
      )}
    </div>
  );
}

function MeasureBreakdown({ bd, ratio, ds, landscapeId }: { bd: LeverBreakdown; ratio: RatioTable; ds: Dataset; landscapeId?: string }) {
  const [openTrace, setOpenTrace] = useState<TraceState | null>(null);
  const personen = useMemo(() => (openTrace ? leverPersons(bd.leverId, ds, ratio, landscapeId) : null), [openTrace, bd.leverId, ds, ratio, landscapeId]);
  const TL: Record<TraceState, string> = { "nie-genutzt": "nie genutzt", genutzt: "genutzt", "kein-trace": "kein Trace" };
  const TC: Record<TraceState, string> = { "nie-genutzt": "var(--color-amp-green)", genutzt: "var(--color-amp-amber)", "kein-trace": "var(--color-amp-gray)" };
  const total = bd.byTrace["nie-genutzt"].persons + bd.byTrace.genutzt.persons + bd.byTrace["kein-trace"].persons || 1;
  return (
    <div className="bg-card mt-2 rounded-lg p-3 text-sm" style={{ borderWidth: 1, borderColor: "var(--border-soft)" }}>
      <div className="font-mono text-[13px] leading-relaxed">
        <div className="text-strong">{bd.authObject} {bd.field} = {bd.value} · Einzelrolle <RoleName id={bd.roleId} name={bd.roleName} /> · {bd.sid}</div>
        <div className="text-normal mt-1"><span className="tnum">{formatNum(bd.affected)}</span> Personen tragen diesen Wert</div>
        <div className="text-dim">− <span className="tnum">{formatNum(bd.heldBack)}</span> bleiben oben, weil ein zweiter Treiber greift</div>
        <div className="text-strong">= <span className="tnum">{formatNum(bd.effective)}</span> stufen sich tatsächlich ab</div>
        {/* Nicht jede Person fällt gleich weit. Bei gemischten Zielklassen wäre
            „effective × deltaPerPerson" falsch — deshalb je Klassenpaar eine Zeile. */}
        {bd.transitions.map((tr) => (
          <div key={`${tr.fromClass}>${tr.toClass}`} className="text-dim mt-1">
            <span className="tnum">{formatNum(tr.persons)}</span> × ({CLASS_LABEL[tr.fromClass]}{" "}
            {formatWeightExact(weight(tr.fromClass, ratio))} → {CLASS_LABEL[tr.toClass]}{" "}
            {formatWeightExact(weight(tr.toClass, ratio))}) = {formatNum(tr.persons)} ×{" "}
            {formatWeightExact(tr.deltaPerPerson)} = −{formatFue(tr.fue)}
          </div>
        ))}
        <div className="font-semibold" style={{ color: "var(--color-amp-green)" }}>
          {bd.mixed ? "Summe" : `${formatNum(bd.effective)} Personen`} = −{formatFue(bd.deltaFue)}
        </div>
        {/* Beide Lesarten mit Namen — die Zahl im Plan („allein") ist so wiedererkennbar. */}
        <div className="text-dim mt-1 flex flex-wrap items-center gap-x-3 text-xs" style={{ fontFamily: "inherit" }}>
          <span>im Verbund <span className="tnum text-strong">{formatNum(bd.effective)}</span> Personen</span>
          <span>allein <span className="tnum text-strong">{formatNum(bd.alone.effective)}</span> Personen</span>
          <InfoTip>„im Verbund": Person fällt, wenn alle entfernbaren Rollen weg sind und diese Rolle auf dem obersten Step liegt (Zahl der Maßnahme). „allein": nur diese Rolle entfernen, alles andere bleibt (Zahl im Plan). Trace je Person = strengster Befund über alle ihre Konten: genutzt vor kein Trace vor nie genutzt.</InfoTip>
        </div>
        {bd.mixed && (
          <div className="text-faint mt-1 text-xs" style={{ fontFamily: "inherit" }}>
            Zwei verschiedene Abstufungen: Wer außer diesem Wert nichts Höheres trägt, fällt bis
            Self-Service — nicht nur bis Core. Deshalb ist die Summe größer als
            {" "}{formatNum(bd.effective)} × {formatWeightExact(bd.deltaPerPerson)}.
          </div>
        )}
      </div>
      <div className="mt-3">
        <div className="text-faint mb-1 text-xs uppercase tracking-wide">Trace-Verteilung (Personen · FUE) · Zeitraum {traceWindowLabel()} · Quelle {TRACE_META.sourceShort} · <span className="normal-case">Klick auf einen Balken zeigt die Personen</span></div>
        {(["nie-genutzt", "genutzt", "kein-trace"] as TraceState[]).map((t) => (
          <button key={t} type="button" onClick={() => setOpenTrace(openTrace === t ? null : t)} className="mb-1 block w-full rounded px-1 text-left hover:bg-sunken" style={openTrace === t ? { backgroundColor: "var(--surface-sunken)" } : undefined} aria-expanded={openTrace === t}>
            <div className="flex justify-between text-xs">
              <span className="inline-flex items-center gap-1.5"><span className="inline-block h-2 w-2 rounded-full" style={{ backgroundColor: TC[t] }} />{TL[t]} {openTrace === t ? <ChevronDown size={12} className="text-faint" /> : <ChevronRight size={12} className="text-faint" />}</span>
              <span className="tnum text-dim">{formatNum(bd.byTrace[t].persons)} · −{formatFue(bd.byTrace[t].fue)}</span>
            </div>
            <div className="bg-sunken mt-0.5 h-1.5 w-full overflow-hidden rounded-full"><div className="h-1.5 rounded-full" style={{ width: `${(bd.byTrace[t].persons / total) * 100}%`, backgroundColor: TC[t] }} /></div>
          </button>
        ))}
        {openTrace && personen && <TracePersonen rows={personen.byTrace[openTrace]} trace={openTrace} />}
      </div>
      <div className="mt-3">
        <div className="text-faint mb-1 text-xs uppercase tracking-wide">Beteiligte Systeme</div>
        <div className="flex flex-wrap gap-1.5">{bd.systems.map((s) => <span key={s.sid} className="bg-sunken rounded px-2 py-0.5 text-xs"><span className="font-mono">{s.sid}</span> <span className="tnum text-dim">{formatNum(s.persons)}</span></span>)}</div>
      </div>
      {/* Systemübergreifend: allein vs. im Verbund — und wer die Zurückgehaltenen hält */}
      <div className="mt-3 rounded-lg p-3" style={{ borderWidth: 1, borderColor: bd.onlyInCombination ? "var(--color-amp-red)" : "var(--color-amp-amber)", backgroundColor: "var(--surface-sunken)" }}>
        <div className="text-strong text-xs font-semibold uppercase tracking-wide">Systemübergreifend — was diese Maßnahme allein bringt</div>
        <div className="mt-1 grid grid-cols-2 gap-3 text-sm">
          <div><div className="text-faint text-[11px] uppercase">nur diese Rolle entfernen</div><div className="tnum text-strong text-lg font-semibold">−{formatFue(bd.alone.deltaFue)}</div><div className="text-faint text-xs">{formatNum(bd.alone.effective)} Personen fallen</div></div>
          <div><div className="text-faint text-[11px] uppercase">zusammen mit den anderen Maßnahmen</div><div className="tnum text-lg font-semibold" style={{ color: "var(--color-amp-green)" }}>−{formatFue(bd.deltaFue)}</div><div className="text-faint text-xs">{formatNum(bd.effective)} Personen fallen</div></div>
        </div>
        {bd.onlyInCombination && <div className="mt-2 text-xs font-medium" style={{ color: "var(--color-amp-red)" }}>Allein unwirksam: jede betroffene Person wird von einer anderen Rolle oben gehalten. Greift nur zusammen mit den unten genannten Maßnahmen.</div>}
        {bd.heldBy.length > 0 && (
          <div className="mt-2">
            <div className="text-faint mb-1 text-[11px] uppercase tracking-wide">Zurückgehaltene · gehalten von · Lösung</div>
            <div className="space-y-1">
              {bd.heldBy.map((h) => (
                <div key={`${h.sid}|${h.roleId}`} className="flex flex-wrap items-center gap-x-2 text-xs">
                  <span className="tnum text-strong w-14 text-right font-semibold">{formatNum(h.persons)}</span>
                  <span className="text-dim">gehalten von</span>
                  <span className="font-mono">{h.sid}</span>
                  <RoleName id={h.roleId} name={h.roleName} />
                  {h.leverId ? <Link to={`/optimierung?lever=${h.leverId}`} className="font-medium hover:underline" style={{ color: "var(--color-brand-600)" }}>→ zusammen mit dieser Maßnahme</Link>
                    : h.roleId === "#DEV#" ? <span className="text-faint">Entwicklerschlüssel — bleibt</span>
                    : h.removable ? <span className="text-faint">entfernbar, aber ohne wirksame Maßnahme — bleibt</span>
                    : <span className="text-faint">nicht entfernbar — diese Personen bleiben oben</span>}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
      {bd.heldExamples.length > 0 && (
        <div className="mt-3">
          <div className="text-faint mb-1 text-xs uppercase tracking-wide">Zurückgehaltene Personen (Auszug)</div>
          <div className="flex flex-wrap gap-1.5">{bd.heldExamples.slice(0, 12).map((h) => <Link key={h.personId} to={`/berechtigungen?person=${h.personId}`} className="bg-sunken rounded px-2 py-0.5 text-xs hover:underline" style={{ color: "var(--color-brand-600)" }}>{h.personId} · {h.sid}</Link>)}</div>
        </div>
      )}
    </div>
  );
}

// ------------------------------------------------------------ Trace-Ampel (inline, kein Shared-Component)
const TRACE_COLOR: Record<TraceState, string> = {
  "nie-genutzt": "var(--color-amp-green)",
  genutzt: "var(--color-amp-amber)",
  "kein-trace": "var(--color-amp-gray)",
};
const TRACE_LABEL: Record<TraceState, string> = {
  "nie-genutzt": "nie genutzt",
  genutzt: "genutzt",
  "kein-trace": "kein Trace",
};
function TraceDot({ trace }: { trace: TraceState }) {
  return (
    <span className="inline-flex items-center gap-1.5">
      <span className="inline-block h-2.5 w-2.5 shrink-0 rounded-full" style={{ backgroundColor: TRACE_COLOR[trace] }} aria-hidden />
      <span className="text-normal text-xs">{TRACE_LABEL[trace]}</span>
    </span>
  );
}

const EFFORT_TONE: Record<string, "green" | "amber" | "red"> = { niedrig: "green", mittel: "amber", hoch: "red" };
const POTENTIAL_TONE: Record<string, "green" | "amber" | "gray"> = { hoch: "green", mittel: "amber", gering: "gray" };

const KEY_LABEL: Record<string, string> = { username: "Benutzername", personalnummer: "Personalnummer", email: "E-Mail", custom: "freies Feld" };

// ------------------------------------------------------------ Vier Ebenen (§4.4)
type Ebene = "wert" | "rolle" | "person" | "landschaft";
const EBENEN: { key: Ebene; label: string; icon: typeof KeyRound; hint: string }[] = [
  { key: "wert", label: "Berechtigungswert", icon: KeyRound, hint: "Einzelner Auth-Wert — der feinste Hebel." },
  { key: "rolle", label: "Einzelrolle", icon: Layers, hint: "Rolle als Träger — gebündelt über ihre Werte." },
  { key: "person", label: "Person", icon: User, hint: "Wer sich abstufen lässt — nach Potenzial." },
  { key: "landschaft", label: "Landschaft", icon: MapIcon, hint: "Wo der optimierbare Bedarf liegt." },
];

/** Eine der drei Kacheln „Systemübergreifend": Zahl, Halbsatz (max. 8 Wörter), ein Satz im InfoTip. */
function CrossTile({ label, value, sub, tip, color, framed }: { label: string; value: string; sub: string; tip: string; color?: string; framed?: boolean }) {
  return (
    <div className="bg-sunken rounded-xl p-4" style={framed ? { borderWidth: 1, borderColor: "var(--color-brand-600)" } : undefined}>
      <div className="text-faint inline-flex items-center gap-1 text-[11px] font-medium uppercase tracking-wide">{label} <InfoTip size={11}>{tip}</InfoTip></div>
      <div className="tnum mt-1 text-2xl font-semibold" style={{ color: color ?? "var(--text-strong)" }}>{value}</div>
      <div className="text-faint mt-1 text-xs">{sub}</div>
    </div>
  );
}

export function Optimierung() {
  const { ds, ratio, price, kpis, identityKey, contracted } = useApp();
  const planItems = usePlanItems();
  const [params, setParams] = useSearchParams();
  const preLever = params.get("lever");
  // Landschaftsfilter: ALLE Karten rechnen nur über die Personen dieser Landschaft.
  const lsFilter = params.get("ls") ?? undefined;
  const lsName = lsFilter ? (assignedLandscapes.find((l) => l.id === lsFilter)?.name ?? lsFilter) : undefined;
  const setLs = (id?: string) => {
    const next = new URLSearchParams(params);
    if (id) next.set("ls", id); else next.delete("ls");
    setParams(next, { replace: true });
  };
  const [ebene, setEbene] = useState<Ebene>("wert");
  const [openBd, setOpenBd] = useState<string | null>(preLever);
  // Absprung „?lever=" (auch aus der Landschaftssicht auf eine Partner-Maßnahme): Rechnung aufklappen.
  useEffect(() => { if (preLever) setOpenBd(preLever); }, [preLever]);
  const [persp, setPersp] = useState<"top" | "trace" | "vogel">("top");

  // ---- Personen im Ausschnitt (Gesamt oder Landschaft) ----
  const scopePersons = useMemo(() => (lsFilter ? ds.persons.filter((p) => p.landscapeIds.includes(lsFilter)) : ds.persons), [ds, lsFilter]);
  const lsSystems = useMemo(() => new Set(lsFilter ? (landscapeById(lsFilter)?.systems ?? []) : []), [lsFilter]);

  // ---- Cluster-Kacheln (aus den Personen des Ausschnitts in EINEM Durchlauf) ----
  const clusters = useMemo(() => {
    let lager = 0, entwickler = 0, extern = 0, karteileichen = 0;
    for (const p of scopePersons) {
      if (p.type === "lager") lager++;
      else if (p.type === "entwickler") entwickler++;
      else if (p.type === "extern") extern++;
      if ((p.lastLogonDays ?? 0) > 90) karteileichen++;
    }
    // Technische Konten hängen an keiner Person — im Filter über die Systeme der Landschaft gezählt.
    const firefighter = lsFilter ? ds.accounts.filter((a) => !a.personId.startsWith("P-") && lsSystems.has(a.sid)).length : ds.technicalCount;
    return { lager, entwickler, extern, karteileichen, firefighter };
  }, [ds, scopePersons, lsFilter, lsSystems]);

  const clusterTiles = [
    { key: "lager", label: "Lager", count: clusters.lager, hint: "Ziel-Klasse Core" },
    { key: "entwickler", label: "Entwickler", count: clusters.entwickler, hint: "Developer · × 2 gewichtet" },
    { key: "extern", label: "Externe", count: clusters.extern, hint: "gemischt, teils befristet" },
    { key: "firefighter", label: "Firefighter / technisch", count: clusters.firefighter, hint: "nicht FUE-relevant (I7)" },
    { key: "karteileichen", label: "Karteileichen", count: clusters.karteileichen, hint: "> 90 Tage ohne Anmeldung" },
  ];

  // ---- Regelgetriebene Ranglisten (analysis.ts) — alle mit demselben Landschaftsausschnitt ----
  const ranked = useMemo(() => leverRanking(ds, ratio, lsFilter), [ds, ratio, lsFilter]);
  // Aufschlüsselung je Maßnahme (strikt/Verbund, Halter) — einmal berechnet, für Zeile und Rechnung.
  const bdById = useMemo(() => new Map(ds.levers.map((l) => [l.id, leverBreakdown(l.id, ds, ratio, lsFilter)] as const)), [ds, ratio, lsFilter]);
  const cross = useMemo(() => crossSystemSummary(ds, ratio, lsFilter), [ds, ratio, lsFilter]);
  const beispiel = useMemo(() => comboExample(ds, lsFilter), [ds, lsFilter]);
  // Landschaftsleiste: vermeidbar allein / im Verbund je FUE-relevanter Landschaft (landscapeDemand).
  const demandAll = useMemo(() => landscapeDemand(ds, ratio), [ds, ratio]);
  const potenzialAlle = useMemo(() => basketAllFue(ds, ratio), [ds, ratio]);
  // Perspektiven — echte MENGEN, nicht nur Sortierungen derselben Liste:
  //  · Aus dem Trace:   überwiegend nie genutzt → Wert entfernen, gefahrlos, kleiner Aufwand
  //  · Umsetzbarkeit:   überwiegend genutzt → Rolle neu schneiden, Prozessentscheidung
  //  · Top:             das Beste aus beiden nach Rang
  type RL = (typeof ranked)[number];
  const PERSPS = [
    { key: "top" as const, label: "Top-Maßnahmen", filter: (_l: RL) => true, pick: (l: RL) => l.rank, num: (l: RL) => l.deltaFue,
      note: "Alle Maßnahmen nach Rang. Rang = Δ FUE × Umsetzbarkeit, der Aufwand ist aus dem Nutzungsanteil berechnet." },
    { key: "trace" as const, label: "Aus dem Trace", filter: (l: RL) => l.unusedShare >= 0.6, pick: (l: RL) => l.safeFue, num: (l: RL) => l.safeFue,
      note: "Maßnahmen, deren Wert überwiegend nie genutzt wurde (Anteil ≥ 60 %). Entzug ist gefahrlos; die Summe zählt nur den nie genutzten Anteil." },
    { key: "vogel" as const, label: "Umsetzbarkeit", filter: (l: RL) => l.unusedShare < 0.6, pick: (l: RL) => l.deltaFue, num: (l: RL) => l.deltaFue,
      note: "Maßnahmen, deren Wert überwiegend genutzt wird. Kein Entzug, sondern Rollen neu schneiden: großer Hebel, aber Prozessentscheidung." },
  ];
  const curPersp = PERSPS.find((p) => p.key === persp) ?? PERSPS[0];
  const perspRanked = ranked.filter(curPersp.filter).sort((a, b) => curPersp.pick(b) - curPersp.pick(a));
  const perspSum = perspRanked.reduce((s, l) => s + curPersp.num(l), 0);
  // Einzelrollen-Rollup: dieselbe Kette, aggregiert je roleId (ranked ist absteigend → erster Treffer ist der Top-Hebel der Rolle).
  const roleGroups = useMemo(() => {
    const m = new Map<string, { roleId: string; roleName: string; sid: string; count: number; affected: number; effective: number; deltaFue: number; rank: number; effort: string; trace: TraceState; landscapeId: string }>();
    for (const l of ranked) {
      const g = m.get(l.roleId);
      if (!g) m.set(l.roleId, { roleId: l.roleId, roleName: l.roleName, sid: l.sid, count: 1, affected: l.affected, effective: l.effective, deltaFue: l.deltaFue, rank: l.rank, effort: l.effort, trace: l.trace, landscapeId: l.landscapeId });
      else {
        g.count++;
        g.affected = Math.max(g.affected, l.affected);
        g.effective = Math.max(g.effective, l.effective);
        g.deltaFue += l.deltaFue;
        g.rank += l.rank;
      }
    }
    return [...m.values()].sort((a, b) => b.rank - a.rank);
  }, [ranked]);
  const persons40 = useMemo(() => rankedPersons(ds, ratio, 40, lsFilter), [ds, ratio, lsFilter]);
  const landDemand = useMemo(
    () => Object.values(demandAll).filter((d) => d.optimizable > 0.5 && (!lsFilter || d.landscapeId === lsFilter)).sort((a, b) => b.optimizable - a.optimizable),
    [demandAll, lsFilter],
  );

  // ---- Vorher/Nachher — Plan als Zielzustand (R4), gerechnet über src/domain/plan.ts ----
  // Gesamt: auf dem aktiven Identitätsschlüssel (I12), dieselbe Bedarfszahl wie Cockpit und Vertrag.
  // Landschaft: Bedarf der Landschaft heute → nach Plan; kein Vertragsvergleich für einen Ausschnitt.
  const plan = useMemo(() => planResult(lsFilter ? { ...ds, persons: scopePersons } : ds, planItems, ratio, price), [ds, scopePersons, lsFilter, planItems, ratio, price, contracted]);
  const lsDemand = lsFilter ? demandAll[lsFilter] : undefined;
  const bedarfAktiv = lsDemand ? lsDemand.fueCurrent : Math.round(kpis.ist);
  const afterPlan = bedarfAktiv - plan.reduction;
  const scaleMax = Math.max(bedarfAktiv, lsFilter ? 0 : contracted, afterPlan) || 1;
  const bars = lsFilter
    ? [
        { label: `Bedarf der Landschaft ${lsName} heute`, value: bedarfAktiv, color: "var(--color-brand-400)" },
        { label: "Zielzustand nach Plan", value: afterPlan, color: "var(--color-brand-500)" },
      ]
    : [
        { label: "Vertrag (Entitlement)", value: contracted, color: "var(--color-brand-600)" },
        { label: `Bedarf heute (${KEY_LABEL[identityKey] ?? identityKey})`, value: bedarfAktiv, color: "var(--color-brand-400)" },
        { label: "Zielzustand nach Plan", value: afterPlan, color: "var(--color-brand-500)" },
      ];
  const restLuecke = afterPlan - contracted;

  // AP4: Optimieren gegen Nachlizenzieren (Business Case) — nur in der Gesamtsicht (Vertragsbezug).
  const nachkaufEuroJahr = kpis.delta * price;
  const gefahrlosEuroJahr = kpis.green * price;
  const restEuroJahr = Math.max(0, restLuecke) * price;

  return (
    <div>
      <PageHeader route="/optimierung" />

      {/* Plan — sticky oben; die Komponente rechnet den Plan selbst (R4). */}
      <PlanBar />

      {/* Landschaftsleiste: eine Zeile, horizontal scrollbar; Klick setzt ?ls= */}
      <div className="scroll-x mb-4 flex gap-2 pb-2" role="tablist" aria-label="Landschaft wählen">
        <button
          type="button"
          role="tab"
          aria-selected={!lsFilter}
          onClick={() => setLs(undefined)}
          className="shrink-0 rounded-xl px-3 py-2 text-left"
          style={!lsFilter ? { borderWidth: 1, borderColor: "var(--color-brand-600)", backgroundColor: "var(--surface-accent)" } : { borderWidth: 1, borderColor: "var(--border-soft)", backgroundColor: "var(--surface-card)" }}
        >
          <div className="text-strong text-sm font-semibold">Alle</div>
          <div className="text-faint tnum text-[11px]">alle Maßnahmen −{formatFue(potenzialAlle)}</div>
        </button>
        {fueLandscapes.map((l) => {
          const d = demandAll[l.id];
          const active = lsFilter === l.id;
          return (
            <button
              key={l.id}
              type="button"
              role="tab"
              aria-selected={active}
              onClick={() => setLs(l.id)}
              className="shrink-0 rounded-xl px-3 py-2 text-left"
              style={active ? { borderWidth: 1, borderColor: "var(--color-brand-600)", backgroundColor: "var(--surface-accent)" } : { borderWidth: 1, borderColor: "var(--border-soft)", backgroundColor: "var(--surface-card)" }}
            >
              <div className="text-strong text-sm font-semibold">{l.name}</div>
              <div className="text-faint tnum text-[11px]">allein −{formatFue(d?.optimizableAlone ?? 0)} · Verbund −{formatFue(d?.optimizable ?? 0)}</div>
            </button>
          );
        })}
      </div>

      {/* Eingeschränkte Sicht sichtbar machen — sonst wirkt eine gefilterte Liste
          wie die Gesamtliste und die Zahlen scheinen zu springen. */}
      {lsFilter && (
        <div
          className="mb-4 flex flex-wrap items-center justify-between gap-2 rounded-xl px-4 py-3 text-sm"
          style={{ borderWidth: 1, borderColor: "var(--color-brand-600)", backgroundColor: "var(--surface-sunken)" }}
        >
          <span className="text-normal inline-flex items-center gap-1">
            Landschaft <span className="text-strong font-semibold">{lsName}</span>
            <span className="text-faint">· {landscapeById(lsFilter)?.systems.join(", ")}</span>
            <InfoTip>Alle Karten dieser Seite rechnen nur über die Personen dieser Landschaft: Systemübergreifend, Cluster, Maßnahmen, Vier Ebenen, Vorher/Nachher. Gesamtzahlen und Vertragsvergleich erscheinen erst wieder unter „Alle Landschaften".</InfoTip>
          </span>
          <button
            type="button"
            onClick={() => setLs(undefined)}
            className="rounded-lg px-3 py-1 text-sm font-medium"
            style={{ borderWidth: 1, borderColor: "var(--border-soft)", color: "var(--color-brand-600)" }}
          >
            Alle Landschaften
          </button>
        </div>
      )}

      {lsFilter && ranked.length === 0 && (
        <div className="mb-4 rounded-xl px-4 py-3 text-sm" style={{ borderWidth: 1, borderColor: "var(--border-soft)", backgroundColor: "var(--surface-sunken)" }}>
          <span className="text-normal font-medium">Keine wirksame Maßnahme in dieser Landschaft.</span>{" "}
          <InfoTip>Die Personen dieser Landschaft tragen keinen der vermeidbaren Über-Grants, oder ein zweiter Treiber hält sie ohnehin oben. Ein Befund, kein fehlender Inhalt.</InfoTip>
        </div>
      )}

      {/* Systemübergreifende Falle: Eine Maßnahme in EINEM System senkt die Person nur, wenn kein
          Konto in einem anderen System oder Mandanten sie oben hält (R2: Klasse = Maximum). */}
      <Card
        title={<span className="inline-flex items-center gap-1">Systemübergreifend{lsName ? ` · ${lsName}` : ""} <InfoTip>
          Die Klasse einer Person ist das Maximum über alle Systeme und Mandanten (R2). Wer nur ein System bereinigt, senkt viele Personen nicht, weil ein Konto anderswo sie oben hält.
          Die Frage ist deshalb nicht „welches System", sondern „welche Rolle in welchen Systemen und Mandanten zusammen". Jede Maßnahme zeigt unter <span className="text-strong font-medium">Rechnung</span>, wer ihre Personen oben hält und welche andere Maßnahme das löst.
        </InfoTip></span>}
        subtitle="Einzeln umgesetzt · Alle zusammen · Nur gemeinsam erreichbar"
        className="mb-6"
      >
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <CrossTile
            label="Einzeln umgesetzt"
            value={`−${formatFue(cross.strictSum)}`}
            sub="jede Maßnahme allein, alles andere bleibt"
            tip="Summe der strikten Wirkungen: jede Maßnahme für sich gerechnet, alle anderen Rollen bleiben bestehen."
          />
          <CrossTile
            label="Alle zusammen"
            value={`−${formatFue(cross.basketAll)}`}
            sub="Zielzustand nach allen Maßnahmen (R4)"
            tip="Zielzustand, wenn alle Maßnahmen umgesetzt sind — jede Person zählt einmal, keine Addition der Einzelwirkungen (R4)."
            color="var(--color-amp-green)"
            framed
          />
          <CrossTile
            label="Nur gemeinsam erreichbar"
            value={formatFue(cross.boundFue)}
            sub="fällt erst mit mehreren Rollen zugleich"
            tip="Differenz der beiden: Personen, die erst fallen, wenn mehrere Rollen auf mehreren Systemen zugleich entfernt sind."
            color="var(--color-amp-amber)"
          />
        </div>
        <div className="text-faint tnum mt-2 text-xs">{formatNum(cross.dependent)} von {formatNum(cross.levers)} Maßnahmen allein schwächer · {formatNum(cross.onlyCombo)} allein unwirksam</div>
        <Aufklapp label="Beispiel" className="mt-2">
          {beispiel ? (
            <span className="inline-flex flex-wrap items-baseline gap-x-1">
              <Link to={`/berechtigungen?person=${beispiel.personId}`} className="font-mono font-medium hover:underline" style={{ color: "var(--color-brand-600)" }}>{beispiel.personId}</Link>
              <span>hält {CLASS_LABEL[beispiel.fromClass]} über</span>
              {beispiel.roles.map((r, i) => (
                <span key={`${r.sid}|${r.roleId}`} className="inline-flex items-baseline gap-x-1">
                  {i > 0 && <span>und</span>}
                  <Link to={`/optimierung?lever=${r.leverId}`} className="hover:underline"><RoleName id={r.roleId} name={r.roleName} /></Link>
                  <span>auf <span className="font-mono">{r.sid}</span></span>
                </span>
              ))}
              <span>— erst {beispiel.roles.length === 2 ? "beide" : "alle"} zusammen senken sie auf {CLASS_LABEL[beispiel.toClass]}.</span>
            </span>
          ) : (
            <span>Im Ausschnitt gibt es keine Person, die zwei entfernbare Rollen auf zwei Systemen trägt und durch keine allein fällt.</span>
          )}
        </Aufklapp>
      </Card>

      <Card title="Cluster" subtitle={lsName ? `Personen je Gruppe · ${lsName}` : "Personen je Gruppe"} className="mb-6">
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
          {clusterTiles.map((c) => (
            <div key={c.key} className="bg-sunken rounded-xl p-3">
              <div className="text-dim text-xs font-medium">{c.label}</div>
              <div className="tnum text-strong mt-1 text-2xl font-semibold">{formatNum(c.count)}</div>
              <div className="text-faint mt-1 text-xs">{c.hint}</div>
            </div>
          ))}
        </div>
      </Card>

      {/* Trace-Ampel erklärt */}
      <Card title="Trace-Legende" subtitle={`Zeitraum ${traceWindowLabel()} · Quelle ${TRACE_META.source}`} className="mb-6">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <div className="flex items-start gap-2.5">
            <span className="mt-1 inline-block h-3 w-3 shrink-0 rounded-full" style={{ backgroundColor: "var(--color-amp-green)" }} />
            <div>
              <div className="text-strong text-sm font-medium">nie genutzt</div>
              <div className="text-dim text-xs">gefahrlos entziehbar</div>
            </div>
          </div>
          <div className="flex items-start gap-2.5">
            <span className="mt-1 inline-block h-3 w-3 shrink-0 rounded-full" style={{ backgroundColor: "var(--color-amp-amber)" }} />
            <div>
              <div className="text-strong text-sm font-medium">genutzt</div>
              <div className="text-dim text-xs">Prozessänderung nötig</div>
            </div>
          </div>
          <div className="flex items-start gap-2.5">
            <span className="mt-1 inline-block h-3 w-3 shrink-0 rounded-full" style={{ backgroundColor: "var(--color-amp-gray)" }} />
            <div>
              <div className="text-strong text-sm font-medium">kein Trace</div>
              <div className="text-dim text-xs">nicht bewertbar</div>
            </div>
          </div>
        </div>
        <Aufklapp label="Methode und Quelle" className="mt-3">
          {TRACE_META.method} „Kein Trace": {TRACE_META.noTrace} Lizenzpflichtig ist, was berechtigt ist; der Trace zeigt, was gefahrlos entziehbar ist. Zeitraum und Quelle stehen deshalb an jeder Trace-Aussage.
        </Aufklapp>
      </Card>

      {/* Maßnahmen + Perspektiven (§3.3) mit anklickbarer Rechnung (§3.1) */}
      <Card
        title={<span className="inline-flex items-center gap-1">Maßnahmen nach Rang{lsName ? ` · ${lsName}` : ""} <InfoTip>
          Rang = Δ FUE × Umsetzbarkeit (Aufwand aus dem Nutzungsanteil) × Trace-Faktor. „Rechnung" legt je Maßnahme die Herleitung offen: Personen, Übergänge, Trace, Systeme, Zurückgehaltene.
          Die Summen der drei Perspektiven sind nicht addierbar, weil dieselbe Person in mehreren Maßnahmen stecken kann; im Plan zählt sie genau einmal (R4).
        </InfoTip></span>}
        subtitle="Rang = Δ FUE × Umsetzbarkeit"
        className="mb-6"
      >
        <div className="mb-3 flex flex-wrap gap-2">
          {PERSPS.map((p) => {
            const active = p.key === persp;
            return (
              <button key={p.key} onClick={() => setPersp(p.key)} className="rounded-lg px-3 py-1.5 text-xs font-medium"
                style={active ? { backgroundColor: "var(--color-brand-600)", color: "#fff" } : { borderWidth: 1, borderColor: "var(--border-soft)", color: "var(--text-dim)" }}>
                {p.label}
              </button>
            );
          })}
        </div>
        <div className="mb-3 flex flex-wrap items-center gap-x-4 gap-y-1 rounded-lg px-3 py-2 text-sm" style={{ backgroundColor: "var(--surface-sunken)" }}>
          <span className="text-dim">{perspRanked.length} Maßnahmen</span>
          <span className="text-strong font-semibold">Σ −{formatFue(perspSum)}</span>
          <InfoTip label="Perspektive erklärt">{curPersp.note}</InfoTip>
        </div>
        <div className="space-y-2">
          {perspRanked.map((lv, i) => {
            const safe = lv.safeFue;
            const safePersons = lv.byTrace["nie-genutzt"].persons;
            const highlight = preLever === lv.id;
            const bdOpen = openBd === lv.id;
            const bd = bdById.get(lv.id);
            const alone = bd?.alone;
            // D3 — Verbund-Bezug: die Maßnahmen, die die Zurückgehaltenen lösen würden (Halter mit Maßnahme).
            const partner = (bd?.heldBy ?? []).filter((h) => h.leverId);
            const verbundGroesser = !!bd && bd.deltaFue > bd.alone.deltaFue + 1e-6;
            return (
              <div key={lv.id} className="rounded-xl" style={{ borderWidth: 1, borderColor: highlight ? "var(--color-brand-400)" : "var(--border-soft)", backgroundColor: highlight ? "var(--surface-accent)" : "var(--surface-sunken)" }}>
                <div className="flex flex-wrap items-start justify-between gap-3 p-3">
                  {/* min-w: die Textspalte darf nicht auf Null schrumpfen — Kennzahlen und Knöpfe brechen dann um. */}
                  <div className="flex min-w-[18rem] flex-1 items-start gap-3">
                    <span className="tnum text-faint mt-0.5 w-6 shrink-0 text-sm font-semibold">#{i + 1}</span>
                    <div className="min-w-0 flex-1">
                      <div className="text-strong font-mono text-[13px] font-medium">{lv.authObject} {lv.field} = {lv.value}</div>
                      <div className="mt-0.5 text-sm"><RoleName id={lv.roleId} name={lv.roleName} /> <span className="text-faint">· {lv.sid} · {lv.landscapeId}</span></div>
                      <div className="text-dim mt-0.5 text-xs">{lv.action}</div>
                      <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1">
                        <TraceDot trace={lv.trace} />
                        <Badge tone={EFFORT_TONE[lv.effort] ?? "neutral"}>Aufwand {lv.effort}</Badge>
                        {!lsFilter && alone && alone.deltaFue < lv.deltaFue - 1e-6 && <Badge tone={alone.effective === 0 ? "red" : "amber"}>{alone.effective === 0 ? "nur im Verbund wirksam" : `allein nur −${formatFue(alone.deltaFue)}`}</Badge>}
                        <span className="tnum text-faint text-xs">Rang {formatNum(lv.rank)}</span>
                      </div>
                      {/* Landschaftssicht: allein −x · mit <Maßnahme> auf <System> −y (verlinkt, ohne ls-Filter) */}
                      {lsFilter && bd && (
                        <div className="mt-1.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs">
                          <span className="tnum text-normal">allein −{formatFue(bd.alone.deltaFue)}</span>
                          {verbundGroesser && partner.length > 0 && (
                            <>
                              <span className="text-faint">·</span>
                              <span className="text-dim">mit</span>
                              {partner.slice(0, 3).map((h, k) => (
                                <span key={`${h.sid}|${h.roleId}`} className="inline-flex items-baseline gap-x-1">
                                  {k > 0 && <span className="text-faint">/</span>}
                                  <Link to={`/optimierung?lever=${h.leverId}`} className="hover:underline" title={`${formatNum(h.persons)} Personen gehalten — Maßnahme ohne Landschaftsfilter öffnen`}>
                                    <RoleName id={h.roleId} name={h.roleName} />
                                  </Link>
                                  <span className="text-dim">auf <span className="font-mono">{h.sid}</span></span>
                                </span>
                              ))}
                              {partner.length > 3 && (
                                <button type="button" onClick={() => setOpenBd(lv.id)} className="text-faint hover:underline">+{partner.length - 3} weitere (Rechnung)</button>
                              )}
                              <span className="tnum font-medium" style={{ color: "var(--color-amp-green)" }}>−{formatFue(bd.deltaFue)}</span>
                            </>
                          )}
                          {verbundGroesser && partner.length === 0 && <span className="text-faint">· Verbund −{formatFue(bd.deltaFue)}, Halter ohne Maßnahme (Rechnung)</span>}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex shrink-0 items-start gap-5 text-right">
                    <div>
                      <div className="text-faint text-[10px] font-medium uppercase tracking-wide">wirksam / betroffen</div>
                      <div className="tnum text-strong text-sm font-semibold">{formatNum(lv.effective)} <span className="text-faint font-normal">/ {formatNum(lv.affected)}</span></div>
                    </div>
                    <div>
                      <div className="text-faint text-[10px] font-medium uppercase tracking-wide">Δ FUE</div>
                      <div className="tnum text-sm font-semibold" style={{ color: "var(--color-amp-green)" }}>−{formatFue(lv.deltaFue)}</div>
                    </div>
                    <div>
                      <div className="text-faint inline-flex items-center gap-0.5 text-[10px] font-medium uppercase tracking-wide">gefahrlos <InfoTip size={11}>Anteil, dessen Berechtigung im Trace-Zeitraum {traceWindowLabel()} nie genutzt wurde. {safe > 0 ? `${formatNum(safePersons)} Personen.` : "Keine Person mit belegter Nichtnutzung."}</InfoTip></div>
                      <div className="tnum text-normal text-sm font-semibold">{safe > 0 ? `−${formatFue(safe)}` : "—"}</div>
                    </div>
                  </div>
                  <div className="flex shrink-0 items-center gap-2">
                    <button type="button" onClick={() => setOpenBd(bdOpen ? null : lv.id)} className="inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-medium" style={{ borderWidth: 1, borderColor: "var(--border-soft)", color: "var(--text-dim)" }}>
                      <Calculator size={14} /> Rechnung {bdOpen ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
                    </button>
                    <PlanButton leverId={lv.id} landscapeId={lsFilter} />
                  </div>
                </div>
                {bdOpen && bd && <div className="px-3 pb-3"><MeasureBreakdown bd={bd} ratio={ratio} ds={ds} landscapeId={lsFilter} /></div>}
              </div>
            );
          })}
        </div>
      </Card>

      {/* Vier Ebenen (§4.4) — dieselbe Kette, umschaltbar */}
      <Card
        title={<span className="inline-flex items-center gap-1">Vier Ebenen derselben Kette{lsName ? ` · ${lsName}` : ""} <InfoTip>
          Wert, Einzelrolle, Person und Landschaft ranken denselben Optimierungspfad aus vier Blickwinkeln. Alle vier Listen leiten aus derselben Klassifikationskette ab, es gibt keinen separaten Datenstand.
        </InfoTip></span>}
        subtitle="Wert · Einzelrolle · Person · Landschaft"
        className="mb-6"
      >
        <div className="flex flex-wrap gap-2">
          {EBENEN.map((e) => {
            const Icon = e.icon;
            const active = ebene === e.key;
            return (
              <button
                key={e.key}
                type="button"
                onClick={() => setEbene(e.key)}
                className="inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm font-medium"
                style={
                  active
                    ? { backgroundColor: "var(--color-brand-600)", color: "#fff" }
                    : { borderWidth: 1, borderColor: "var(--border-soft)", color: "var(--text-dim)" }
                }
              >
                <Icon size={15} /> {e.label}
              </button>
            );
          })}
        </div>
        <div className="text-faint mt-2 text-xs">{EBENEN.find((e) => e.key === ebene)?.hint}</div>

        <div className="scroll-x mt-4 w-full">
          {/* Ebene: Berechtigungswert */}
          {ebene === "wert" && (
            <table className="w-full border-collapse text-sm">
              <thead>
                <tr className="text-faint text-left text-xs uppercase tracking-wide">
                  <th className="px-2 py-2 font-medium">Wert</th>
                  <th className="px-2 py-2 text-right font-medium">Betroffen</th>
                  <th className="px-2 py-2 text-right font-medium">Wirksam</th>
                  <th className="px-2 py-2 text-right font-medium">Δ FUE</th>
                  <th className="px-2 py-2 font-medium">Aufwand</th>
                  <th className="px-2 py-2 font-medium">Risiko</th>
                  <th className="px-2 py-2 text-right font-medium">Rang</th>
                </tr>
              </thead>
              <tbody>
                {ranked.map((lv) => (
                  <tr key={lv.id} className="border-t border-soft align-top" style={preLever === lv.id ? { backgroundColor: "var(--surface-accent)" } : undefined}>
                    <td className="px-2 py-3">
                      <div className="text-normal font-medium">{lv.authObject} · {lv.field} = {lv.value}</div>
                      <div className="text-xs"><RoleName id={lv.roleId} name={lv.roleName} /> <span className="text-faint">· {lv.sid} · {lv.landscapeId}</span></div>
                    </td>
                    <td className="px-2 py-3 text-right"><span className="tnum text-dim">{formatNum(lv.affected)}</span></td>
                    <td className="px-2 py-3 text-right"><span className="tnum text-normal">{formatNum(lv.effective)}</span></td>
                    <td className="px-2 py-3 text-right"><span className="tnum font-semibold" style={{ color: "var(--color-amp-green)" }}>−{formatFue(lv.deltaFue)}</span></td>
                    <td className="px-2 py-3"><Badge tone={EFFORT_TONE[lv.effort] ?? "neutral"}>{lv.effort}</Badge></td>
                    <td className="px-2 py-3"><TraceDot trace={lv.trace} /></td>
                    <td className="px-2 py-3 text-right"><span className="tnum text-normal font-medium">{formatNum(lv.rank)}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          {/* Ebene: Einzelrolle */}
          {ebene === "rolle" && (
            <table className="w-full border-collapse text-sm">
              <thead>
                <tr className="text-faint text-left text-xs uppercase tracking-wide">
                  <th className="px-2 py-2 font-medium">Einzelrolle</th>
                  <th className="px-2 py-2 text-right font-medium">Betroffen</th>
                  <th className="px-2 py-2 text-right font-medium">Wirksam</th>
                  <th className="px-2 py-2 text-right font-medium">Δ FUE</th>
                  <th className="px-2 py-2 font-medium">Aufwand</th>
                  <th className="px-2 py-2 font-medium">Risiko</th>
                  <th className="px-2 py-2 text-right font-medium">Rang</th>
                </tr>
              </thead>
              <tbody>
                {roleGroups.map((g) => (
                  <tr key={g.roleId} className="border-t border-soft align-top">
                    <td className="px-2 py-3">
                      <div><RoleName id={g.roleId} name={g.roleName} /></div>
                      <div className="text-faint text-xs">{g.count} {g.count === 1 ? "Wert" : "Werte"} · {g.sid} · {g.landscapeId}</div>
                    </td>
                    <td className="px-2 py-3 text-right"><span className="tnum text-dim">{formatNum(g.affected)}</span></td>
                    <td className="px-2 py-3 text-right"><span className="tnum text-normal">{formatNum(g.effective)}</span></td>
                    <td className="px-2 py-3 text-right"><span className="tnum font-semibold" style={{ color: "var(--color-amp-green)" }}>−{formatFue(g.deltaFue)}</span></td>
                    <td className="px-2 py-3"><Badge tone={EFFORT_TONE[g.effort] ?? "neutral"}>{g.effort}</Badge></td>
                    <td className="px-2 py-3"><TraceDot trace={g.trace} /></td>
                    <td className="px-2 py-3 text-right"><span className="tnum text-normal font-medium">{formatNum(g.rank)}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          {/* Ebene: Person */}
          {ebene === "person" && (
            <table className="w-full border-collapse text-sm">
              <thead>
                <tr className="text-faint text-left text-xs uppercase tracking-wide">
                  <th className="px-2 py-2 font-medium">Person</th>
                  <th className="px-2 py-2 font-medium">Potenzial</th>
                  <th className="px-2 py-2 font-medium">Klassenwechsel</th>
                  <th className="px-2 py-2 font-medium">Treibersystem</th>
                  <th className="px-2 py-2 text-right font-medium">Δ FUE</th>
                </tr>
              </thead>
              <tbody>
                {persons40.map((rp) => (
                  <tr key={rp.person.id} className="border-t border-soft align-top">
                    <td className="px-2 py-3">
                      <Link to={`/berechtigungen?person=${rp.person.id}`} className="text-normal font-medium hover:underline" style={{ color: "var(--color-brand-600)" }}>
                        {rp.person.id}
                      </Link>
                      <div className="text-faint text-xs">{rp.person.unit}</div>
                    </td>
                    <td className="px-2 py-3"><Badge tone={POTENTIAL_TONE[rp.potential] ?? "neutral"}>{rp.potential}</Badge></td>
                    <td className="px-2 py-3">
                      <span className="inline-flex items-center gap-1">
                        <KlasseBadge cls={rp.fromClass} />
                        <ArrowRight size={12} className="text-faint" />
                        <KlasseBadge cls={rp.toClass} />
                      </span>
                    </td>
                    <td className="px-2 py-3"><span className="text-dim">{rp.drivingSystem}</span></td>
                    <td className="px-2 py-3 text-right"><span className="tnum font-semibold" style={{ color: "var(--color-amp-green)" }}>−{formatFue(rp.deltaFue)}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          {/* Ebene: Landschaft */}
          {ebene === "landschaft" && (
            <table className="w-full border-collapse text-sm">
              <thead>
                <tr className="text-faint text-left text-xs uppercase tracking-wide">
                  <th className="px-2 py-2 font-medium">Landschaft</th>
                  <th className="px-2 py-2 text-right font-medium">Personen</th>
                  <th className="px-2 py-2 text-right font-medium">allein</th>
                  <th className="px-2 py-2 text-right font-medium">im Verbund</th>
                  <th className="px-2 py-2 text-right font-medium">davon gefahrlos</th>
                </tr>
              </thead>
              <tbody>
                {landDemand.map((d) => (
                  <tr key={d.landscapeId} className="border-t border-soft align-top">
                    <td className="px-2 py-3">
                      <Link to={`/landschaften?ls=${d.landscapeId}`} className="text-normal font-medium hover:underline" style={{ color: "var(--color-brand-600)" }}>
                        {landscapeById(d.landscapeId)?.name ?? d.landscapeId}
                      </Link>
                    </td>
                    <td className="px-2 py-3 text-right"><span className="tnum text-dim">{formatNum(d.persons)}</span></td>
                    <td className="px-2 py-3 text-right"><span className="tnum text-normal">−{formatFue(d.optimizableAlone)}</span></td>
                    <td className="px-2 py-3 text-right"><span className="tnum font-semibold" style={{ color: "var(--color-amp-green)" }}>−{formatFue(d.optimizable)}</span></td>
                    <td className="px-2 py-3 text-right"><span className="tnum text-normal">{formatFue(d.optimizableSafe)}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </Card>

      {/* Vorher / Nachher */}
      <Card
        title={<span className="inline-flex items-center gap-1">Vorher / Nachher{lsName ? ` · ${lsName}` : ""} <InfoTip>
          {lsFilter
            ? "Bedarf der Landschaft heute (Personen mit Bezug zu dieser Landschaft) und Zielzustand nach den Maßnahmen im Plan, gerechnet über dieselben Personen (R4). Ein Vertragsvergleich gilt nur für das Ganze, nicht für einen Ausschnitt."
            : "Bedarf heute auf dem aktiven Identitätsschlüssel; der Plan senkt davon ausgehend als Zielzustand (R4). Restlücke = Zielzustand − Vertrag."}
        </InfoTip></span>}
        subtitle={lsFilter ? "Bedarf der Landschaft heute · Zielzustand nach Plan" : "Vertrag · Bedarf heute · Zielzustand nach Plan"}
      >
        <div className="space-y-3">
          {bars.map((b) => (
            <div key={b.label}>
              <div className="mb-1 flex items-baseline justify-between text-sm">
                <span className="text-dim">{b.label}</span>
                <span className="tnum text-normal font-medium">{formatFue(b.value)}</span>
              </div>
              <div className="bg-sunken h-3 w-full overflow-hidden rounded-full">
                <div className="h-3 rounded-full" style={{ width: `${(b.value / scaleMax) * 100}%`, backgroundColor: b.color }} />
              </div>
            </div>
          ))}
        </div>

        {lsFilter && lsDemand ? (
          <div className="mt-5 rounded-xl p-4" style={{ backgroundColor: "var(--surface-sunken)" }}>
            <div className="flex flex-wrap items-baseline justify-between gap-2">
              <span className="text-strong text-sm font-semibold">Plan in dieser Landschaft</span>
              <span className="tnum text-xl font-semibold" style={{ color: "var(--color-amp-green)" }}>−{formatFue(plan.reduction)}</span>
            </div>
            <div className="text-dim tnum mt-1 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs">
              <span>Bedarf heute {formatFue(bedarfAktiv)} → nach Plan {formatFue(afterPlan)}</span>
              <span>nach allen Maßnahmen {formatFue(lsDemand.fueClean)}</span>
              <span>allein in dieser Landschaft erreichbar −{formatFue(lsDemand.optimizableAlone)}</span>
            </div>
          </div>
        ) : (
          <div className="mt-5 rounded-xl p-4" style={{ backgroundColor: "var(--surface-sunken)" }}>
            <div className="flex flex-wrap items-baseline justify-between gap-2">
              <span className="text-strong text-sm font-semibold">Restlücke</span>
              <span className="tnum text-xl font-semibold" style={{ color: restLuecke > 0 ? "var(--color-amp-red)" : "var(--color-amp-green)" }}>
                {restLuecke > 0 ? "+" : ""}{formatFue(restLuecke)}
              </span>
            </div>
            <div className="text-dim mt-1 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs">
              <span className="tnum">Zielzustand {formatFue(afterPlan)} − Vertrag {formatFue(contracted)}</span>
              {restLuecke > 0 && <span className="tnum">≈ {formatEuroMio(restLuecke * price)} / Jahr bei {formatNum(price)} € / FUE</span>}
              {restLuecke <= 0 && <span>Zielzustand liegt unter dem Vertrag</span>}
            </div>

            {/* A3.1 — Restlücke erklären: drei Wege, statt nur „bleibt bestehen". */}
            {restLuecke > 0 && (
              <Aufklapp label="Wege zur Schließung der Restlücke" className="mt-3">
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                  {[
                    { t: "Tieferer Rollenumbau", d: "Genutzte Grants entziehen. Erfordert Prozessänderung und Abstimmung mit den Fachbereichen." },
                    { t: "Nachlizenzierung", d: "Verbleibenden Bedarf einkaufen. Planbar, aber wiederkehrende Kosten." },
                    { t: "Verhandlung", d: "Metrik und Gewichte mit SAP klären, z. B. Lager-Klassifizierung." },
                  ].map((w) => (
                    <div key={w.t} className="bg-card rounded-lg p-3" style={{ borderWidth: 1, borderColor: "var(--border-soft)" }}>
                      <div className="text-strong text-sm font-semibold">{w.t}</div>
                      <div className="text-dim mt-1 text-xs">{w.d}</div>
                    </div>
                  ))}
                </div>
              </Aufklapp>
            )}
          </div>
        )}
      </Card>

      {/* AP4: Optimieren gegen Nachlizenzieren — RISE ist kein Kaufmodell. Nur in der Gesamtsicht (Vertragsbezug). */}
      {!lsFilter && (
        <Card
          title={<span className="inline-flex items-center gap-1">Optimieren gegen Nachlizenzieren <InfoTip>
            Nachlizenzieren kostet das Delta jedes Jahr neu, ohne dass sich am Berechtigungsstand etwas ändert. Gefahrlose Optimierung senkt den Bedarf dauerhaft gegen einmaligen Projektaufwand; der verbleibende Rest ist die Grundlage für die Verhandlung.
          </InfoTip></span>}
          subtitle="€ / Jahr"
          className="mt-6"
        >
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="rounded-xl p-4" style={{ borderWidth: 1, borderColor: "var(--color-amp-red)" }}>
              <div className="text-strong text-sm font-semibold">Einfach nachlizenzieren</div>
              <div className="tnum mt-2 text-3xl font-semibold" style={{ color: "var(--color-amp-red)" }}>{formatEuroMio(nachkaufEuroJahr)}</div>
              <div className="text-dim mt-1 text-sm">pro Jahr — <span className="font-medium">jedes Jahr</span></div>
              <div className="tnum text-faint mt-2 text-xs">{formatFue(kpis.delta)} FUE Delta × {formatNum(price)} € / FUE</div>
            </div>
            <div className="rounded-xl p-4" style={{ borderWidth: 1, borderColor: "var(--color-amp-green)" }}>
              <div className="text-strong text-sm font-semibold">Gefahrlos optimieren</div>
              <div className="tnum mt-2 text-3xl font-semibold" style={{ color: "var(--color-amp-green)" }}>−{formatEuroMio(gefahrlosEuroJahr)}</div>
              <div className="text-dim mt-1 text-sm">pro Jahr, dauerhaft gesenkt</div>
              <div className="tnum text-faint mt-2 text-xs">{formatFue(kpis.green)} FUE nie genutzt × {formatNum(price)} € / FUE · Rest {formatFue(Math.max(0, restLuecke))} ≈ {formatEuroMio(restEuroJahr)} / Jahr</div>
            </div>
          </div>
        </Card>
      )}
    </div>
  );
}
